import { useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// Auth/membership propagation: Supabase usually issues both within ~100ms.
// We retry briefly to absorb cold-start jitter, then fall back to
// activation-repair-user as a self-healing safety net.
const MEMBERSHIP_RETRY_DELAYS = [0, 200, 400, 800];

interface AuthRoutingState {
  session: Session | null;
  loading: boolean;
  hasTenant: boolean | null;
  onboardingCompleted: boolean | null;
  /** True when both the trigger AND activation-repair-user failed to provision. */
  repairFailed: boolean;
  /** Last failure reason surfaced from activation-repair-user, when available. */
  repairReason: string | null;
  /** Manually retry the repair flow (used by the recovery card). */
  retryRepair: () => void;
}

type MembershipResult = { found: boolean; onboardingDone: boolean; reason: string | null };

export function useAuthRoutingState(): AuthRoutingState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasTenant, setHasTenant] = useState<boolean | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [repairFailed, setRepairFailed] = useState(false);
  const [repairReason, setRepairReason] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let active = true;
    const wait = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

    const queryMembership = async (userId: string): Promise<MembershipResult> => {
      const { data: membership, error: membershipError } = await supabase
        .from("tenant_memberships")
        .select("tenant_id")
        .eq("user_id", userId)
        .maybeSingle();

      if (membershipError || !membership?.tenant_id) {
        return { found: false, onboardingDone: false, reason: null };
      }

      const { data: tenant, error: tenantError } = await supabase
        .from("tenants")
        .select("onboarding_completed")
        .eq("id", membership.tenant_id)
        .maybeSingle();

      if (tenantError) return { found: false, onboardingDone: false, reason: null };
      return { found: true, onboardingDone: tenant?.onboarding_completed ?? false, reason: null };
    };

    const resolveTenantMembership = async (
      userId: string,
      requestId: number,
    ): Promise<MembershipResult> => {
      // Initial retry budget (covers normal trigger jitter)
      for (const delay of MEMBERSHIP_RETRY_DELAYS) {
        if (delay > 0) await wait(delay);
        if (!active || requestId !== requestIdRef.current) {
          return { found: false, onboardingDone: false, reason: null };
        }

        const result = await queryMembership(userId);
        if (!active || requestId !== requestIdRef.current) {
          return { found: false, onboardingDone: false, reason: null };
        }
        if (result.found) return result;
      }

      // Fallback: invoke activation-repair-user (idempotent re-provisioning)
      let lastReason: string | null = null;
      try {
        let trialDomain: string | null = null;
        try {
          trialDomain =
            (typeof localStorage !== "undefined" && localStorage.getItem("autopitch.trial_domain")) ||
            (typeof sessionStorage !== "undefined" && sessionStorage.getItem("autopitch.trial_domain")) ||
            null;
        } catch { /* ignore */ }

        const { data: repairData, error: repairError } = await supabase.functions.invoke(
          "activation-repair-user",
          { body: { trial_domain: trialDomain } },
        );
        if (!active || requestId !== requestIdRef.current) {
          return { found: false, onboardingDone: false, reason: null };
        }

        if (repairError) {
          console.warn("[auth-routing] repair invocation failed:", repairError);
          lastReason = repairError.message ?? "invoke_failed";
        } else if (repairData && !repairData.tenant_id) {
          console.warn("[auth-routing] repair returned no tenant:", repairData);
          lastReason = (repairData?.reason as string | undefined) ?? "no_tenant";
        } else {
          console.info("[auth-routing] repair result:", repairData);
        }

        // Re-query once after repair
        await wait(150);
        const recheck = await queryMembership(userId);
        if (recheck.found) return recheck;
      } catch (e) {
        console.warn("[auth-routing] repair fatal:", e);
        lastReason = e instanceof Error ? e.message : "repair_fatal";
      }

      return { found: false, onboardingDone: false, reason: lastReason };
    };

    const syncSession = async (nextSession: Session | null) => {
      if (!active) return;

      const requestId = ++requestIdRef.current;
      setLoading(true);
      setRepairFailed(false);
      setRepairReason(null);

      if (!nextSession?.user) {
        setSession(null);
        setHasTenant(null);
        setOnboardingCompleted(null);
        setLoading(false);
        return;
      }

      setHasTenant(null);
      setOnboardingCompleted(null);
      setSession(nextSession);

      const { found, onboardingDone, reason } = await resolveTenantMembership(nextSession.user.id, requestId);

      if (active && requestId === requestIdRef.current) {
        setHasTenant(found);
        setOnboardingCompleted(onboardingDone);
        setRepairFailed(!found);
        setRepairReason(reason);
        setLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      window.setTimeout(() => {
        void syncSession(nextSession);
      }, 0);
    });

    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      void syncSession(initialSession);
    });

    return () => {
      active = false;
      requestIdRef.current += 1;
      subscription.unsubscribe();
    };
  }, [retryNonce]);

  return {
    session,
    loading,
    hasTenant,
    onboardingCompleted,
    repairFailed,
    repairReason,
    retryRepair: () => setRetryNonce((n) => n + 1),
  };
}
